'use client';

import Image from 'next/image';
import React from 'react';
import { getInitials, isValidUrl } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import LegalExpertResource from '@/types/api/legalExperts.types';

export const HireExpertsSidePanel = ({
  legalExperts,
}: {
  legalExperts: LegalExpertResource[];
}) => {
  const t = useI18n();

  return (
    <div className='hide-scrollbar h-full overflow-y-auto p-2 bg-white dark:bg-gray-900/95'>
      <div className='flex items-center p-2'>
        <span>{starIcon}</span>
        <span className='ml-4 text-lg font-bold text-gray-700 dark:text-gray-100'>
          {t('experts.title')}
        </span>
      </div>
      <div className='space-y-4'>
        {legalExperts.map((expert) => (
          <div
            key={expert.id}
            className='space-y-4 rounded-lg border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 p-4 sm:min-w-[354px]'
          >
            <div className='flex items-center justify-between'>
              <div className='flex space-x-4'>
                <div className='relative h-12 w-12 overflow-hidden rounded-md'>
                  {expert.expert_image &&
                  expert.expert_image.length > 0 &&
                  isValidUrl(expert.expert_image) ? (
                    <Image
                      src={expert.expert_image}
                      alt={`${expert.name} Image`}
                      fill
                      sizes='(max-width: 768px) 10vw, (max-width: 1200px) 50vw, 33vw'
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <div className='flex h-full w-full items-center justify-center border bg-purple-600 dark:bg-purple-500 font-medium text-white'>
                      {getInitials(expert.name)}
                    </div>
                  )}
                </div>
                <div>
                  <div className='text-lg font-bold text-gray-700 dark:text-gray-100'>
                    {expert.name}
                  </div>
                  <div className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                    {expert.location_formatted}
                  </div>
                </div>
              </div>
              <div className='flex items-center space-x-1.5 text-sm font-medium text-gray-700 dark:text-gray-300'>
                <span>{ratingIcon}</span>
                <span>{expert.rating.score}</span>
                <span>{`(${expert.rating.total_ratings})`}</span>
              </div>
            </div>
            <p className='break-words border-b border-gray-200 dark:border-gray-700/50 pb-4 text-xs text-gray-500 dark:text-gray-400'>
              {expert.description}
            </p>
            <div className='flex flex-wrap space-x-2'>
              {expert.fields_of_expertise.main_fields.map((field) => (
                <span
                  key={field}
                  className='h-6 break-words rounded-[20px] border border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-700/50 px-4 py-1 text-xs font-medium text-gray-500 dark:text-gray-300'
                >
                  {field}
                </span>
              ))}
              <span className='h-6 break-words rounded-[20px] border border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-700/50 px-4 py-1 text-xs font-medium text-gray-500 dark:text-gray-300'>
                {expert.fields_of_expertise.total_remaining_formatted}
              </span>
            </div>
            <button className='h-10 w-full rounded bg-gradient-to-l from-purple-300 to-purple-600 dark:from-purple-400 dark:to-purple-700 p-2 text-sm font-medium text-white hover:opacity-90 transition-opacity'>
              {t('__form.freeQuote')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const starIcon = (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='text-purple-600 dark:text-purple-400'
  >
    <path
      d='M9.48002 20.69C9.09998 20.6998 8.72734 20.5838 8.42002 20.36C8.02002 20.07 7.40002 19.32 7.79002 17.64L8.36002 15.18C8.39198 14.9121 8.31647 14.6424 8.15002 14.43L6.15702 12.44C5.80181 12.1607 5.53526 11.7843 5.38971 11.3566C5.24416 10.9288 5.22586 10.468 5.33702 10.03C5.49854 9.60838 5.77937 9.24294 6.14521 8.97833C6.51104 8.71372 6.94603 8.56141 7.39702 8.54L9.94702 8.12C10.1867 8.05302 10.3933 7.8999 10.527 7.69L11.937 4.87C12.0887 4.43548 12.3655 4.05553 12.7326 3.77795C13.0996 3.50036 13.5406 3.33755 14 3.31C14.4584 3.3387 14.898 3.50204 15.264 3.77955C15.6299 4.05705 15.9058 4.43638 16.057 4.87L17.467 7.69C17.6029 7.89787 17.8086 8.05037 18.047 8.12L20.597 8.54C21.0485 8.55951 21.4844 8.71109 21.8506 8.97594C22.2167 9.2408 22.4971 9.6073 22.657 10.03C22.7682 10.468 22.7499 10.9288 22.6043 11.3566C22.4588 11.7843 22.1922 12.1607 21.837 12.44L19.847 14.43C19.764 14.5348 19.7027 14.6551 19.6667 14.7839C19.6306 14.9127 19.6205 15.0473 19.637 15.18L20.207 17.64C20.597 19.33 19.977 20.07 19.577 20.36C19.177 20.65 18.287 21.02 16.787 20.13L14.4 18.71C14.2715 18.6507 14.1316 18.62 13.99 18.62C13.8485 18.62 13.7086 18.6507 13.58 18.71L11.19 20.13C10.6819 20.4694 10.0905 20.6631 9.48002 20.69ZM14 4.81C13.84 4.81 13.55 5 13.28 5.54L11.87 8.36C11.6985 8.67755 11.4589 8.95334 11.1685 9.16768C10.8781 9.38202 10.544 9.52964 10.19 9.6L7.64002 10.02C7.07002 10.12 6.81002 10.33 6.76002 10.49C6.71002 10.65 6.80002 10.97 7.21002 11.38L9.20002 13.37C9.46133 13.6548 9.65326 13.9961 9.76081 14.3673C9.86836 14.7386 9.88863 15.1296 9.82002 15.51L9.25002 17.97C9.08002 18.68 9.19002 19.06 9.30002 19.15C9.41002 19.24 9.81002 19.22 10.43 18.84L12.83 17.42C13.1886 17.2232 13.591 17.1201 14 17.1201C14.409 17.1201 14.8114 17.2232 15.17 17.42L17.56 18.84C18.19 19.21 18.59 19.23 18.7 19.15C18.81 19.07 18.92 18.69 18.75 17.97L18.18 15.51C18.1082 15.1297 18.127 14.7378 18.2347 14.366C18.3424 13.9943 18.536 13.6531 18.8 13.37L20.79 11.38C21.2 10.97 21.29 10.64 21.24 10.49C21.19 10.34 20.93 10.12 20.36 10.02L17.81 9.6C17.456 9.52964 17.1219 9.38202 16.8315 9.16768C16.5411 8.95334 16.3016 8.67755 16.13 8.36L14.72 5.54C14.45 5 14.157 4.81 14 4.81Z'
      fill='currentColor'
    />
    <path
      d='M8 5.75H2C1.80149 5.74869 1.61149 5.66925 1.47112 5.52888C1.33075 5.38851 1.25131 5.19851 1.25 5C1.25131 4.80149 1.33075 4.61149 1.47112 4.47112C1.61149 4.33075 1.80149 4.25131 2 4.25H8C8.19851 4.25131 8.38851 4.33075 8.52888 4.47112C8.66925 4.61149 8.74869 4.80149 8.75 5C8.74869 5.19851 8.66925 5.38851 8.52888 5.52888C8.38851 5.66925 8.19851 5.74869 8 5.75Z'
      fill='currentColor'
    />
    <path
      d='M5 19.75H2C1.80149 19.7487 1.61149 19.6693 1.47112 19.5289C1.33075 19.3885 1.25131 19.1985 1.25 19C1.25131 18.8015 1.33075 18.6115 1.47112 18.4711C1.61149 18.3307 1.80149 18.2513 2 18.25H5C5.19851 18.2513 5.38851 18.3307 5.52888 18.4711C5.66925 18.6115 5.74869 18.8015 5.75 19C5.74869 19.1985 5.66925 19.3885 5.52888 19.5289C5.38851 19.6693 5.19851 19.7487 5 19.75Z'
      fill='currentColor'
    />
    <path
      d='M3 12.75H2C1.80149 12.7487 1.61149 12.6693 1.47112 12.5289C1.33075 12.3885 1.25131 12.1985 1.25 12C1.25131 11.8015 1.33075 11.6115 1.47112 11.4711C1.61149 11.3307 1.80149 11.2513 2 11.25H3C3.19851 11.2513 3.38851 11.3307 3.52888 11.4711C3.66925 11.6115 3.74869 11.8015 3.75 12C3.74869 12.1985 3.66925 12.3885 3.52888 12.5289C3.38851 12.6693 3.19851 12.7487 3 12.75Z'
      fill='currentColor'
    />
  </svg>
);

const ratingIcon = (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='text-[#F7A440] dark:text-[#FFB86C]'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M11.0137 2.76683C11.379 1.89022 12.6208 1.89022 12.9861 2.76683L14.9102 7.38462C15.0654 7.75726 15.4295 8 15.8332 8H20.893C21.8234 8 22.2893 9.12483 21.6314 9.78268L17.5391 13.875C17.2823 14.1318 17.185 14.5076 17.2847 14.8568L18.9076 20.5369C19.1816 21.496 18.1122 22.2767 17.2822 21.7234L12.5546 18.5716C12.2187 18.3477 11.7811 18.3477 11.4452 18.5717L6.72544 21.7182C5.89284 22.2732 4.81988 21.49 5.09479 20.5279L6.71509 14.8568C6.81486 14.5076 6.71747 14.1318 6.46068 13.875L2.38859 9.8029C1.72328 9.13758 2.19448 8 3.13538 8H8.16658C8.57028 8 8.93438 7.75726 9.08965 7.38462L11.0137 2.76683Z'
      fill='currentColor'
    />
  </svg>
);
